/**
 * Submission view: read a persisted submission back by id and show its per-order result.
 *
 * Reached at `/document-reader/{submissionId}` — both as the redirect target straight after
 * an upload and as a bookmarkable URL revisited later. It is driven purely by
 * `GET /storage/submissions/{id}`, which carries the extraction (fields, confidence, source
 * text) and each order's shipment number and push result — but NOT the pre-push validation
 * issues, resolved locations or price. Those live only on the one-time submit response, so
 * the table here is built with `validation: null`, `locations: []` and `price: null`.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { fetchSubmission } from './api';
import { buildGroupsFromExtraction, EM_DASH, isLeanMode } from './fields';
import { OrderTable } from './order-table';
import { messageOf, Stat } from './shared';
import { TokenBridge } from './token-bridge';
import './styles.css';

function Chrome({ children }: { children: React.ReactNode }) {
	return (
		<div className="document-reader">
			<TokenBridge />
			<header className="topbar">
				<div className="topbar-inner">
					<div className="brand">
						<h1>Document reader</h1>
						<p>Submission result</p>
					</div>
					<Link to="/document-reader" className="pill-link">
						Upload another document
					</Link>
				</div>
			</header>
			<main className="main">{children}</main>
		</div>
	);
}

export function SubmissionView({ submissionId }: { submissionId: string }) {
	// TanStack Query owns the fetch: it dedupes the in-flight request by key, so the
	// StrictMode double-mount and Clerk's auth-settling re-renders collapse to one call,
	// and a re-render never refires it. `retry: false` keeps a real failure to a single call.
	const {
		data: response,
		isPending,
		isError,
		error,
	} = useQuery({
		queryKey: ['document-reader-submission', submissionId],
		queryFn: () => fetchSubmission(submissionId),
		retry: false,
		refetchOnWindowFocus: false,
		// A stored submission is immutable, so it never needs re-reading once loaded —
		// this stops a remount from firing a background refetch.
		staleTime: Number.POSITIVE_INFINITY,
	});

	if (isPending) {
		return (
			<Chrome>
				<section className="card">
					<div className="status" role="status" aria-live="polite" aria-busy>
						<div className="progress" />
						<p className="status-text">Loading submission…</p>
					</div>
				</section>
			</Chrome>
		);
	}

	if (isError) {
		return (
			<Chrome>
				<section className="card">
					<p className="banner banner-error">
						<strong>Could not load this submission.</strong> {messageOf(error)}
					</p>
				</section>
			</Chrome>
		);
	}

	const submission = response.submission ?? null;

	if (!response.store_extractions) {
		return (
			<Chrome>
				<section className="card">
					<p className="banner banner-warn">
						<strong>Submissions are not stored on this server</strong>{' '}
						(STORE_EXTRACTIONS=false), so this result cannot be shown.
					</p>
				</section>
			</Chrome>
		);
	}

	if (submission === null) {
		return (
			<Chrome>
				<section className="card">
					<p className="banner banner-warn">
						<strong>Submission not found.</strong> No submission exists for{' '}
						<code>{submissionId}</code>.
					</p>
				</section>
			</Chrome>
		);
	}

	const extracted = submission.extracted_document;
	const lean = extracted !== null && isLeanMode(extracted);
	const hasConfidence = extracted !== null && !lean;
	const total = submission.orders.length;
	const acceptedCount = submission.orders.filter((order) => order.api_ok).length;

	return (
		<Chrome>
			<section className="card" aria-labelledby="result-heading">
				<h2 id="result-heading">Result</h2>

				<dl className="stats">
					<Stat label="File" value={submission.filename} />
					<Stat label="Status" value={submission.status} />
					<Stat label="Created" value={submission.created_at} />
					<Stat
						label="Customer"
						value={
							submission.customer_alias ?? submission.customer_key ?? EM_DASH
						}
					/>
					<Stat label="Containers" value={String(total)} />
					<Stat label="Registered" value={`${acceptedCount} / ${total}`} />
				</dl>

				{acceptedCount === total ? (
					<p className="banner banner-ok">
						<strong>
							{total} {total === 1 ? 'container' : 'containers'} registered with
							UTURN.
						</strong>
					</p>
				) : (
					<p className="banner banner-error">
						<strong>
							{total - acceptedCount} of {total} containers were rejected.
						</strong>{' '}
						See the relevant order below for the reason.
					</p>
				)}

				{lean && (
					<p className="banner banner-warn">
						<strong>Confidence and source text are disabled on the server</strong>{' '}
						(CONFIDENCE_SOURCETEXT=false). Every field reports 1.0; that is not a
						measured reliability, so the Confidence and Source columns stay empty.
					</p>
				)}

				{extracted === null && (
					<p className="banner banner-warn">
						<strong>Field-level extraction is not available</strong> for this
						submission, so only the per-order push result is shown.
					</p>
				)}

				{submission.orders.map((order) => {
					const extractedOrder = extracted?.orders?.[order.order_index] ?? null;
					const containerNumber =
						extractedOrder?.container?.container_number?.value ??
						order.container_number;
					// A numeric shipment number links to its detail page. Simulated runs
					// return a non-numeric `FAKE-…` id that would not resolve, so those stay
					// a plain badge.
					const shipmentNumber =
						order.uturn_shipment_number !== null &&
						/^\d+$/.test(order.uturn_shipment_number)
							? Number(order.uturn_shipment_number)
							: null;

					return (
						<article key={order.submitted_order_id} className="order">
							<header className="order-head">
								<div>
									<h3>
										Order {order.order_index + 1} of {total}
										{containerNumber !== null && (
											<span className="order-container">{containerNumber}</span>
										)}
									</h3>
								</div>
								{order.api_ok ? (
									<p className="pill pill-ok">
										Registered
										{order.uturn_shipment_number !== null &&
											(shipmentNumber !== null ? (
												<Link
													to="/shipments/$shipmentNumber"
													params={{ shipmentNumber }}
													className="pill-link"
												>
													{order.uturn_shipment_number}
												</Link>
											) : (
												<code>{order.uturn_shipment_number}</code>
											))}
									</p>
								) : (
									<p className="pill pill-bad">Rejected</p>
								)}
							</header>

							{!order.api_ok && order.api_errors.length > 0 && (
								<div className="banner banner-error">
									<strong>This container was not registered with UTURN.</strong>
									<ul className="issues">
										{order.api_errors.map((message, index) => (
											<li key={index} className="issue sev-error">
												<span>{message}</span>
											</li>
										))}
									</ul>
								</div>
							)}

							{extractedOrder !== null && (
								<OrderTable
									groups={buildGroupsFromExtraction(
										extractedOrder,
										null,
										[],
										null,
										order.request_json?.locationActions ?? [],
									)}
									hasConfidence={hasConfidence}
								/>
							)}
						</article>
					);
				})}

				<details className="tech">
					<summary>Technical details</summary>
					<dl className="stats">
						<Stat label="Submission id" value={submission.submission_id} />
						<Stat label="Updated" value={submission.updated_at} />
						<Stat
							label="Document SHA-256"
							value={submission.document_sha256}
						/>
					</dl>
				</details>
			</section>
		</Chrome>
	);
}
